"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import {
  scoreStatusFromResult,
  type ProblemScoreStatus,
} from "../lib/problem-score-status";

export type { ProblemScoreStatus } from "../lib/problem-score-status";

// 점수 비율을 0~1 로 클램프한다(부분점수 정규화 공용).
const clamp01 = (n: number) => Math.min(Math.max(n, 0), 1);

interface ExamScoreContextType {
  correctCount: number;
  totalAttempted: number;
  problemResults: Record<number, ProblemScoreStatus>;
  problemResultsById: Record<string, ProblemScoreStatus>;
  addAttempt: (
    isCorrect: boolean,
    problemNumber?: number,
    scoreRatio?: number,
    problemId?: string,
  ) => void;
  resetScore: () => void;
}

const ExamScoreContext = createContext<ExamScoreContextType | undefined>(
  undefined
);

export const useExamScore = () => {
  const context = useContext(ExamScoreContext);
  return context; // Context가 없으면 undefined 반환
};

interface ExamScoreProviderProps {
  children: ReactNode;
  initialProblemResultsById?: Record<string, ProblemScoreStatus>;
  // 복원된 문제별 정확 점수 비율(0~1). 부분점수를 status 근사 대신 정확히 카운터에 반영(JCG-2806).
  initialProblemScoreById?: Record<string, number>;
}

export const ExamScoreProvider: React.FC<ExamScoreProviderProps> = ({
  children,
  initialProblemResultsById,
  initialProblemScoreById,
}) => {
  const [correctCount, setCorrectCount] = useState(0);
  const [totalAttempted, setTotalAttempted] = useState(0);
  const [problemResults, setProblemResults] = useState<Record<number, ProblemScoreStatus>>({});
  const [sessionProblemResultsById, setSessionProblemResultsById] = useState<
    Record<string, ProblemScoreStatus>
  >({});

  // 이 마운트에서 점수 카운터(correctCount/totalAttempted)에 이미 반영된 문제 id 집합.
  // 세션 채점(addAttempt)과 복원 폴딩(아래 effect)이 같은 문제를 이중집계하지 않도록 게이트한다.
  // ref 는 컴포넌트 인스턴스별이라 리마운트 시 자동으로 비워진다(카운터 useState 0 리셋과 정합).
  const countedProblemIdsRef = useRef<Set<string>>(new Set());

  // 문제별로 카운터(correctCount)에 이미 반영한 점수(0~1).
  // 같은 문제를 다시 채점하면 이전 기여분을 빼고 새 값을 더해 "최신 채점 결과 1건"만 남긴다.
  const countedScoreByIdRef = useRef<Map<string, number>>(new Map());

  const problemResultsById = useMemo(
    () => ({
      ...(initialProblemResultsById ?? {}),
      ...sessionProblemResultsById,
    }),
    [initialProblemResultsById, sessionProblemResultsById],
  );

  const addAttempt = (
    isCorrect: boolean,
    problemNumber?: number,
    scoreRatio?: number,
    problemId?: string,
  ) => {
    const normalizedScore =
      typeof scoreRatio === "number"
        ? clamp01(scoreRatio)
        : isCorrect
          ? 1
          : 0;
    const status = scoreStatusFromResult(isCorrect, scoreRatio);

    if (problemId) {
      // 같은 문제의 재채점은 새 제출로 세지 않고 직전 결과를 대체한다.
      // (countedProblemIdsRef 주석의 "세션 채점과 복원 폴딩의 이중집계 방지" 계약을 addAttempt 쪽에서도 지킨다.)
      if (!countedProblemIdsRef.current.has(problemId)) {
        countedProblemIdsRef.current.add(problemId);
        setTotalAttempted((prev) => prev + 1);
      }
      const previousScore = countedScoreByIdRef.current.get(problemId) ?? 0;
      countedScoreByIdRef.current.set(problemId, normalizedScore);
      const scoreDelta = normalizedScore - previousScore;
      if (scoreDelta !== 0) {
        setCorrectCount((prev) => prev + scoreDelta);
      }
      setSessionProblemResultsById((prev) => ({
        ...prev,
        [problemId]: status,
      }));
    } else {
      // problemId 를 넘기지 않는 레거시 호출부는 기존 누적 동작을 유지한다.
      setTotalAttempted((prev) => prev + 1);
      if (normalizedScore > 0) {
        setCorrectCount((prev) => prev + normalizedScore);
      }
    }

    if (problemNumber != null) {
      setProblemResults((prev) => ({
        ...prev,
        [problemNumber]: status,
      }));
    }
  };

  // 리사이즈 리마운트(LayoutSwitch 셸↔클래식 교체, JCG-2806) 복원: RandomizedProblems 가
  // localStorage 에서 되살린 문제별 채점상태(initialProblemResultsById)를 점수 카운터에도 반영한다.
  // 복원 데이터는 effect 로 마운트 후 도착하므로 useState 초기값으로는 시드할 수 없어, 도착 시점에
  // 아직 세지 않은 항목만 1회 접어 넣는다(ref dedup — 세션 재제출/재렌더 이중집계 방지).
  // 부분점수는 저장된 정확 비율(initialProblemScoreById)이 있으면 그대로, 없으면(레거시) status 로
  // 0.5 근사한다. correct=1/incorrect=0 은 status 만으로 정확하다.
  useEffect(() => {
    const restored = initialProblemResultsById;
    if (!restored) return;
    let addTotal = 0;
    let addCorrect = 0;
    for (const [id, status] of Object.entries(restored)) {
      if (countedProblemIdsRef.current.has(id)) continue;
      countedProblemIdsRef.current.add(id);
      addTotal += 1;
      const exactRatio = initialProblemScoreById?.[id];
      const restoredScore =
        typeof exactRatio === "number"
          ? clamp01(exactRatio)
          : status === "correct"
            ? 1
            : status === "partial"
              ? 0.5
              : 0;
      // 복원분도 문제별 기여분으로 기록해야, 이후 같은 문제를 다시 채점할 때 delta 가 맞는다.
      countedScoreByIdRef.current.set(id, restoredScore);
      addCorrect += restoredScore;
    }
    // 마운트 후 도착하는 복원 데이터를 ref dedup 으로 1회만 접어 넣는 의도된 동기화
    // (RandomizedProblems.tsx 의 동일 패턴과 계약 일치).
    if (addTotal > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTotalAttempted((prev) => prev + addTotal);
      setCorrectCount((prev) => prev + addCorrect);
    }
  }, [initialProblemResultsById, initialProblemScoreById]);

  const resetScore = () => {
    setCorrectCount(0);
    setTotalAttempted(0);
    setProblemResults({});
    setSessionProblemResultsById({});
    countedProblemIdsRef.current.clear();
    countedScoreByIdRef.current.clear();
  };

  return (
    <ExamScoreContext.Provider
      value={{
        correctCount,
        totalAttempted,
        problemResults,
        problemResultsById,
        addAttempt,
        resetScore,
      }}
    >
      {children}
    </ExamScoreContext.Provider>
  );
};
